import { useEffect } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog"

export interface LightboxItem {
  src: string
  alt: string
  title: string
  subtitle?: string | null
}

interface PhotoLightboxProps {
  items: LightboxItem[]
  /** null = cerrado */
  index: number | null
  onIndexChange: (index: number | null) => void
}

// Muestra una foto completa (object-contain, sin recorte) con navegación entre
// las fotos de la misma colección. Las flechas del teclado cambian de foto y
// Escape cierra (lo maneja el Dialog).
export function PhotoLightbox({ items, index, onIndexChange }: PhotoLightboxProps) {
  const open = index !== null && items[index] !== undefined
  const total = items.length

  useEffect(() => {
    if (index === null || total < 2) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault()
        onIndexChange((index - 1 + total) % total)
      } else if (e.key === "ArrowRight") {
        e.preventDefault()
        onIndexChange((index + 1) % total)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [index, total, onIndexChange])

  const item = open && index !== null ? items[index] : null

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onIndexChange(null)}>
      <DialogContent className="max-w-[min(94vw,56rem)] gap-3 p-3 sm:max-w-3xl sm:p-4">
        {item && (
          <>
            <div className="flex items-center justify-center rounded-lg bg-muted">
              <img
                src={item.src}
                alt={item.alt}
                decoding="async"
                className="max-h-[70dvh] w-auto max-w-full rounded-lg object-contain"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <DialogTitle className="text-base font-semibold text-foreground">{item.title}</DialogTitle>
                {item.subtitle ? (
                  <DialogDescription className="text-sm text-muted-foreground">{item.subtitle}</DialogDescription>
                ) : (
                  <DialogDescription className="sr-only">Foto ampliada</DialogDescription>
                )}
              </div>
              {total > 1 && index !== null && (
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => onIndexChange((index - 1 + total) % total)}
                    aria-label="Foto anterior"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-border text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring sm:h-9 sm:w-9"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <span className="min-w-[3.5rem] text-center text-xs tabular-nums text-muted-foreground" aria-live="polite">
                    {index + 1} de {total}
                  </span>
                  <button
                    type="button"
                    onClick={() => onIndexChange((index + 1) % total)}
                    aria-label="Foto siguiente"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-border text-foreground outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring sm:h-9 sm:w-9"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
